import { Tooltip, Whisper } from 'rsuite';

/** Strip HTML tags and collapse whitespace — for rich-text columns. */
const stripHtmlTags = (value: string): string =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

type TruncatedTextOptions = {
  /** Characters to keep before truncating. Default 80. */
  maxLength?: number;
  /** Shown when the value is empty. Default '-'. */
  placeholder?: string;
  /** Strip HTML tags before measuring (for rich-text columns). */
  stripTags?: boolean;
  /** Class on the rendered span. Default 'requests-table__cell-text'. */
  className?: string;
  /** Show the tooltip even when the text was not truncated. */
  forceTooltip?: boolean;
};

/**
 * Renders a table-cell value truncated to `maxLength`, with the full text in
 * a hover tooltip when it was cut. Shared across the admin list pages so cell
 * truncation looks and behaves the same everywhere.
 *
 * @param value
 * @param options
 */
export function truncatedTextCell(
  value: unknown,
  options: TruncatedTextOptions = {}
) {
  const {
    maxLength = 80,
    placeholder = '-',
    stripTags = false,
    className = 'requests-table__cell-text',
    forceTooltip = false,
  } = options;

  const raw = String(value ?? '');
  const normalized = stripTags ? stripHtmlTags(raw) : raw.trim();

  if (!normalized) {
    return <span className={className}>{placeholder}</span>;
  }

  const preview =
    normalized.length > maxLength
      ? `${normalized.slice(0, maxLength - 1)}…`
      : normalized;

  const content = (
    <span className={className} title={normalized}>
      {preview}
    </span>
  );

  if (preview === normalized && !forceTooltip) {
    return content;
  }

  return (
    <Whisper
      placement="top"
      trigger="hover"
      speaker={<Tooltip>{normalized}</Tooltip>}
    >
      {content}
    </Whisper>
  );
}
